// HTTP client real apontando pro backend Fase 2.
// MSW intercepta em dev se VITE_API_BASE_URL não estiver setada.

import { i18n } from '@app-iptv/i18n'
import type {
  CuratedItem,
  EnrichedSeriesItem,
  EnrichedVodItem,
  EpgProgram,
  LiveCategory,
  LiveChannel,
  ProviderBranding,
  Series,
  SignedStreamUrl,
  VodItem,
} from '@app-iptv/iptv-client'

import { clearTokens, getTokens, isTokenExpired, setTokens } from './auth-store'
import { catalogStore } from './catalog-store'
import type { SurfaceName, SurfaceValueMap } from './catalog-store'
import { getDeviceInfo } from './fingerprint'
import { isDeadlineError, makeNetDeadline } from './lib/deadline'
import { beginCritical, endCritical, runLowPriority } from './net-priority'
import { reportCacheHit, reportCacheMiss } from './qoe'
import { useSessionStore } from './store'
import { authenticatedRequestLacksSwipeBearer } from './swipe-bearer-rule'
import { getValidSwipeAccessToken, hasSwipeSession } from './sync/swipe-session'

// ===== Search v2.1.8.3 — types compartilhados com backend =====
// Mantidos client-side pra evitar dep cruzada @app-iptv/api → tv.

export interface SearchFiltersClient {
  genres?: string[]
  years?: string[]
  platforms?: string[]
  ageRatings?: string[]
  audios?: string[]
  qualities?: string[]
  minRating?: number
}

export interface AvailableFiltersClient {
  genres: string[]
  years: string[]
  platforms: string[]
  ageRatings: string[]
  audios: string[]
  qualities: string[]
  ratings: number[]
}

export interface SearchResponseClient {
  channels: LiveChannel[]
  vod: VodItem[]
  series: Series[]
  availableFilters: AvailableFiltersClient
  appliedFilters: SearchFiltersClient
  /** Total que TERIA aparecido sem filtros (Wave A.1). UX "X de Y". */
  unfilteredCount: number
}

// ===== Snapshot helpers (Fase 3.7) — local-first =====
// Usa providerCode (ou listName direto) como chave de snapshot. Backend
// identifica providerId pelo JWT; cliente só precisa de chave estável.
// Exportado pra reuso em outros stores (ex: vod-info-store).
export function getProviderKey(): string | null {
  return getSnapshotKey()
}
function getSnapshotKey(): string | null {
  const s = useSessionStore.getState()
  if (s.providerCode) return s.providerCode
  if (s.direct?.listName) return `direct:${s.direct.listName}`
  return null
}

/**
 * Lê UMA surface do snapshot local (DB v2). Devolve null quando não há
 * snapshot válido pra este provider — o chamador cai na rede (revalidação).
 *
 * INVARIANTE: catálogo é local-first; a rede é revalidação, nunca caminho
 * crítico. Toda leitora de catálogo deve tentar a surface ANTES do request.
 *
 * Isolamento de provider: a chave vem SEMPRE de getSnapshotKey() (sessão
 * corrente) — nunca de parâmetro do chamador. Snapshot de outro provider é
 * inalcançável por construção: chave diferente = record diferente.
 *
 * Só conta cache HIT quando a leitura de fato serviu a tela; miss/ausente é
 * contabilizado pelo chamador (reportCacheMiss) com a latência da rede.
 */
async function readSurface<K extends SurfaceName, T = SurfaceValueMap[K]>(
  surface: K,
  fn?: (value: SurfaceValueMap[K]) => T,
): Promise<T | null> {
  const key = getSnapshotKey()
  if (!key) return null
  const start = performance.now()
  const meta = await catalogStore.getMeta(key)
  if (!meta) return null
  if (catalogStore.isStale(meta)) return null
  const value = await catalogStore.getSurface(key, surface)
  if (!value) return null
  const result = fn ? fn(value) : (value as unknown as T)
  reportCacheHit(performance.now() - start)
  return result
}

// URL base é DINÂMICA — vem do runtime-config (carregado no boot).
// Permite failover entre N endpoints sem rebuild quando domínio principal cai.
// Em testes/SSR sem runtime-config, cai no env build-time. Ver runtime-config.ts.
// Exportada pra a camada de sync (swipe-session) usar a MESMA base/failover.
export function getApiBase(): string {
  try {
    return getActiveApiUrlLazy()
  } catch {
    return (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? ''
  }
}

// URL do site B2C — DINÂMICA, vem do runtime-config (campo `siteUrl`, carregado
// no boot). Fonte de verdade em prod é o env `SITE_URL` da API. Camadas de
// fallback: runtime-config → VITE_SITE_URL (build) → literal de prod.
//
// É GANCHO LAZY (função, NÃO const top-level de módulo): const congelaria o
// fallback no import, ANTES de wireRuntimeConfig rodar no boot. Chamar no
// render/uso. NUNCA retorna undefined — o QR de pareamento e os links de
// compra precisam de valor no 1º paint, mesmo se o fetch de config falhar.
// Mesmo padrão de getApiBase() acima.
export function getSiteUrl(): string {
  try {
    return getActiveSiteUrlLazy()
  } catch {
    return (import.meta.env['VITE_SITE_URL'] as string | undefined) ?? 'https://swipetvplayer.com'
  }
}
// Ref pra função (preenchido no bootstrap pra evitar import cycle).
let getActiveApiUrlLazy: () => string = () =>
  (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? ''
let getActiveSiteUrlLazy: () => string = () =>
  (import.meta.env['VITE_SITE_URL'] as string | undefined) ?? 'https://swipetvplayer.com'
let rotateApiUrlLazy: () => boolean = () => false
let resetApiUrlLazy: () => void = () => {}
/**
 * Plug do runtime-config no api.ts. Chamado UMA VEZ pelo bootstrap após
 * loadRuntimeConfig() resolver. Evita import cycle (api.ts → runtime-config
 * → api.ts não acontece porque importamos via referência).
 */
export function wireRuntimeConfig(deps: {
  getActiveApiUrl: () => string
  getActiveSiteUrl: () => string
  rotateApiUrl: () => boolean
  resetApiUrl: () => void
}): void {
  getActiveApiUrlLazy = deps.getActiveApiUrl
  getActiveSiteUrlLazy = deps.getActiveSiteUrl
  rotateApiUrlLazy = deps.rotateApiUrl
  resetApiUrlLazy = deps.resetApiUrl
}

interface ListEnvelope<T> {
  items: T[]
  page?: number
  pageSize?: number
  totalItems?: number
  totalPages?: number
}

export interface GroupedChannel {
  key: string
  name: string
  categoryId: string
  logo: string | null
  isAdult: boolean
  hasArchive: boolean
  epgChannelId?: string
  variants: Array<{
    id: string
    quality: string | null
    codec: string | null
    language: string | null
  }>
}

interface AuthResponse {
  accessToken: string
  refreshToken: string
  expiresIn: number
  profile?: { username: string }
  branding?: ProviderBranding
  providerCode?: string
  accountExpiresAt?: number | null
}

let refreshInflight: Promise<void> | null = null

// Hook de "sessão inválida" — registrado pelo app pra deslogar do store.
// `reason` discrimina:
//  - 'session_expired': JWT/refresh token inválido (401, refresh falhou).
//    Credenciais ainda válidas — não esquece login memory.
//  - 'account_expired': plano do user vencido no Xtream (403 com
//    error: 'account_expired' em rota auth). Credenciais válidas, só
//    o plano acabou — também não esquece login memory.
export type AuthInvalidReason = 'session_expired' | 'account_expired'
let onAuthInvalidHandler: ((reason: AuthInvalidReason) => void) | null = null
// Idempotência: múltiplas queries paralelas podem disparar 401/403
// no mesmo ciclo de invalidação. `notifyAuthInvalid` é gating pra que
// só a PRIMEIRA chame o handler. Reset no setOnAuthInvalid (novo
// handler = nova sessão) e no clearTokens explícito do app
// (re-login bem-sucedido reabilita). Sem isso, 5 queries em paralelo
// → 5 logout()s + 5 navigate()s redundantes.
let alreadyInvalidated = false
/** Registra handler. Retorna unsubscriber pra unbind (útil em testes
 *  / hot-reload — sem isso, handlers stale vazam entre re-mounts). */
export function setOnAuthInvalid(
  handler: (reason: AuthInvalidReason) => void,
): () => void {
  onAuthInvalidHandler = handler
  alreadyInvalidated = false
  return () => {
    if (onAuthInvalidHandler === handler) {
      onAuthInvalidHandler = null
    }
  }
}
/** Reseta o gate de invalidação. Chamar após setAuth bem-sucedido. */
export function resetAuthInvalidGate(): void {
  alreadyInvalidated = false
}
function notifyAuthInvalid(reason: AuthInvalidReason = 'session_expired'): void {
  if (alreadyInvalidated) return
  alreadyInvalidated = true
  if (onAuthInvalidHandler) onAuthInvalidHandler(reason)
}

/**
 * Resolve o Bearer das rotas autenticadas (I3+I4 — refactor de identidade).
 *
 * - **Com sessão Swipe** (conta cloud logada): usa o token SWIPE
 *   (`getValidSwipeAccessToken`, que renova via /v1/swipe-account/refresh com
 *   rotation atômica). O backend é bi-identidade (I1/I2): resolve o providerId
 *   server-side pela FONTE ATIVA VALIDADA da conta. Esta é a FONTE ÚNICA de
 *   refresh quando logado na conta — o caminho Xtream nem é tocado (sem corrida).
 * - **Sem sessão Swipe** (base legada, ainda não pareou): usa o token XTREAM do
 *   auth-store, como antes. O backend ainda aceita `enduser` até a I7.
 *
 * Os dois stores são SEPARADOS (memória de projeto): o token Swipe nunca é
 * escrito no auth-store Xtream nem vice-versa.
 *
 * Exportada (I3+I4 — fix A1) pra fetchers que vivem FORA do `request()` —
 * ex.: catalog-snapshot.ts faz fetch próprio (stream + medição de banda) e
 * precisa do MESMO Bearer bi-identidade pra não cair em 401 no_provider quando
 * o user entra pela conta Swipe sem token Xtream válido.
 */
export async function swipeAuthHeader(): Promise<string | null> {
  if (hasSwipeSession()) {
    // Fonte única quando logado na conta: getValidSwipeAccessToken já renova.
    return getValidSwipeAccessToken()
  }
  await refreshIfNeeded()
  const tokens = getTokens()
  return tokens ? tokens.accessToken : null
}

/** Prazo do refresh do token Xtream legado. Mais curto que o default de 15s dos
 *  requests: ele bloqueia a montagem do header de TODO fetcher autenticado, e o
 *  desfecho de "não respondeu" é seguir com o token atual, não falhar. */
const REFRESH_TIMEOUT_MS = 12_000

async function refreshIfNeeded(): Promise<void> {
  // Com sessão Swipe, o refresh relevante é o do Swipe (feito por
  // getValidSwipeAccessToken). NÃO mexer no token Xtream legado — evita
  // corrida entre os dois caminhos de refresh.
  if (hasSwipeSession()) return
  if (!isTokenExpired()) return
  const tokens = getTokens()
  if (!tokens) return

  if (!refreshInflight) {
    refreshInflight = (async () => {
      // Este fetch nasceu FORA do `request()` e por isso não herdou o
      // AbortController de lá — era um `await` de rede sem terminação garantida
      // no caminho de TODO fetcher autenticado, inclusive o do snapshot
      // (`swipeAuthHeader` → `refreshIfNeeded`). Pendurado aqui, o catálogo nem
      // chegava a ser pedido.
      const net = makeNetDeadline(REFRESH_TIMEOUT_MS, 'auth/refresh')
      try {
        const base: RequestInit = {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ refreshToken: tokens.refreshToken }),
        }
        // `exactOptionalPropertyTypes`: só inclui `signal` quando a global
        // existe (no build de TV existe — polyfill antes do entry). Sem ela, o
        // race contra `net.expired` cobre igual.
        const init: RequestInit = net.signal ? { ...base, signal: net.signal } : base
        const res = await Promise.race([
          fetch(`${getApiBase()}/v1/auth/refresh`, init),
          net.expired,
        ])
        if (res.ok) {
          const body = (await Promise.race([res.json(), net.expired])) as AuthResponse
          setTokens(body.accessToken, body.refreshToken, body.expiresIn)
        } else {
          clearTokens()
          notifyAuthInvalid('session_expired')
        }
      } catch {
        // ⚠️ FALHA DE REDE NÃO É SESSÃO INVÁLIDA — e NENHUM erro que chega neste
        // `catch` é "o servidor disse que teu token morreu". Aqui caem prazo
        // estourado, Wi-Fi fora, DNS, CORS e body malformado. O único sinal
        // autoritativo é o `else` do `res.ok` acima, e ele já desloga.
        //
        // Antes, um `fetch` que rejeitasse limpava o token: a TV deslogava
        // sozinha porque o roteador piscou. Seguimos com o token atual — se ele
        // estiver mesmo expirado, o 401 do próximo `request()` trata pelo
        // caminho normal (ver o handler de 401 mais abaixo, que faz
        // `clearTokens` + `notifyAuthInvalid('session_expired')`), aí sim com
        // resposta do servidor.
        //
        // Mesma regra que a telemetria já aplica: perder uma tentativa é
        // gratuito; deslogar o usuário por rede caída, não.
      } finally {
        net.clear()
        refreshInflight = null
      }
    })()
  }
  await refreshInflight
}

/**
 * Timeout default por request. fetch nativo não tem timeout, então
 * usamos AbortController. Backend já tem timeout 10s no Xtream upstream;
 * 15s no cliente cobre o pior caso (cold cache + Xtream lento) com
 * margem. Pode ser sobrescrito por chamada via init.signal próprio.
 */
const DEFAULT_TIMEOUT_MS = 15_000

/**
 * Códigos de 401 que NÃO falam da NOSSA sessão — e por isso não podem derrubá-la.
 *
 * O 401 desta API é sobrecarregado: ele tanto diz "teu token morreu" (identidade)
 * quanto "o painel do PROVEDOR recusou a credencial Xtream que você mandou no
 * corpo" (dado de terceiro). Só o primeiro é motivo de deslogar. Tratar os dois
 * igual foi o defeito medido: uma senha Xtream errada no formulário de fonte
 * expulsava o usuário da CONTA SWIPE, com a mensagem errada ("sessão expirada"),
 * e `errorToMessage` já tinha a tradução certa (`invalid_xtream_credentials` →
 * "credenciais inválidas") pra uma tela que nunca chegava a mostrá-la.
 *
 *  - `no_provider`: conta válida, sem fonte ativa (I3+I4) — configuração, não
 *    identidade. Já escapava; agora está no mesmo lugar que os irmãos.
 *  - `invalid_xtream_credentials`: o PUT preference revalida user/senha contra o
 *    painel Xtream. O painel recusar (inclusive o `auth:0` intermitente que
 *    provedor devolve sob carga) diz zero sobre o nosso token.
 *
 * Fica como LISTA e não como flag por chamada de propósito: a regra é do CÓDIGO,
 * vale pra todo mundo que receber esse erro, e não da tela que por acaso chamou.
 */
const NAO_INVALIDAM_SESSAO = ['no_provider', 'invalid_xtream_credentials'] as const

async function request<T>(
  path: string,
  init: RequestInit & { auth?: boolean; timeoutMs?: number; silentAuthFailure?: boolean } = {},
): Promise<T> {
  const {
    auth = false,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    silentAuthFailure = false,
    signal: callerSignal,
    ...rest
  } = init
  // Resolve o Bearer (renova Swipe ou Xtream conforme a sessão) ANTES de montar
  // os headers. swipeAuthHeader centraliza a escolha de identidade (I3+I4).
  const bearer = auth ? await swipeAuthHeader() : null
  // Com sessão Swipe e SEM Bearer, o pedido autenticado não sai (ver swipe-bearer-rule.ts):
  // pra quem chamou é falha transitória de rede (`timeout`), sem notificar invalidação.
  if (authenticatedRequestLacksSwipeBearer(auth, bearer)) throw new ApiError('timeout', 0)

  const headers: Record<string, string> = {
    'content-type': 'application/json',
    // Backend usa pra TMDB enrichment (sinopse/genre/poster traduzidos).
    // i18n.language sempre tem valor após setupI18n() do boot.
    'accept-language': i18n.language || 'pt-BR',
    ...((rest.headers as Record<string, string>) ?? {}),
  }
  if (auth) {
    if (bearer) headers['authorization'] = `Bearer ${bearer}`
    // Tier do dispositivo — backend cap size de listas + skip enrichment
    // pesado em vlow/low. Lê do dataset.tier que bootstrap.ts setou no
    // <html>. Header só em rotas autenticadas (não-auth não vê tier).
    if (typeof document !== 'undefined') {
      const tier = document.documentElement.dataset['tier']
      if (tier) headers['x-device-tier'] = tier
    }
  }

  // Prazo por RACE, não por `setTimeout(() => ctrl.abort())`.
  //
  // O que havia aqui era `const ctrl = new AbortController()` + um timer que
  // chamava `ctrl.abort()`. Isso NÃO tem terminação garantida na faixa Chr42-63
  // do fleet (webOS 4.x/Chr53, Tizen 2017-2018): lá o `fetch` é o NATIVO — o
  // `whatwg-fetch` só se instala sob `if (!g.fetch)` e `fetch` existe desde o
  // Chr42 — e o nativo só entende `signal` a partir do Chr66. Membro
  // desconhecido de dictionary WebIDL é descartado EM SILÊNCIO, então o abort
  // não fazia nada e o `await` abaixo não assentava por prazo NENHUM. Medido em
  // Chromium 53 real: o mesmo par pendurava indefinidamente.
  //
  // `makeNetDeadline` é a mesma peça que o `auth/refresh` acima já usa: o
  // `net.expired` corre contra o `fetch` e é ele que LIBERA o await; o abort
  // continua junto (solta o socket onde o motor o respeita), como extra.
  // É o invariante de `lib/deadline.ts`: nenhum await sem terminação garantida.
  //
  // O prazo segue sendo TOTAL (uma promessa só, criada fora do laço), não por
  // tentativa — igual ao timer único de antes.
  const net = makeNetDeadline(timeoutMs, `api ${path}`)
  // Signal do caller (unmount, troca de tela): antecipa o prazo pra AGORA em vez
  // de abortar direto. `bump(0)` dispara o mesmo caminho do prazo — aborta o
  // socket E rejeita `net.expired` —, então o cancelamento também assenta na
  // faixa onde o abort é inerte.
  //
  // ⚠️ O DESFECHO PRO CALLER é o mesmo de antes (`AbortError` já era mapeado pra
  // `ApiError('timeout', 0)` logo abaixo), mas o EFEITO NA REDE mudou num caso, e
  // isso é medido, não suposto:
  //
  //   caller que chega com `signal.aborted === true`
  //     antes  (`ctrl.abort()` síncrono): no instante do fetch já estava
  //            abortado, então o request NÃO saía;
  //     agora  (`bump(0)`): `arm(0)` agenda `fire` num `setTimeout`, e não há
  //            `await` entre esta linha e o `fetch` do laço — no instante do
  //            fetch `aborted` ainda é false, o request SAI, e só um macrotask
  //            depois é abortado.
  //
  // Hoje isso é LATENTE, não vivo: não há caller alcançável que entregue um
  // signal já abortado por aqui (os laços de `services/pairing.ts` guardam com
  // `while (!signal.aborted)` e sequer repassam o signal). Se um dia houver, o
  // conserto é abortar de forma SÍNCRONA além do `bump(0)` — não trocar o
  // `bump(0)` de volta por `ctrl.abort()`, que reabriria o pendura da faixa
  // Chr42-63.
  if (callerSignal) {
    if (callerSignal.aborted) net.bump(0)
    else callerSignal.addEventListener('abort', () => net.bump(0), { once: true })
  }

  // Failover: tenta a URL ativa; se 5xx ou network error, rotaciona pra
  // próxima do runtime-config e re-tenta. Garante max(N) tentativas onde
  // N = endpoints.length (rotateApiUrl retorna false ao esgotar).
  // 4xx NÃO rotaciona (problema de request, não de endpoint).
  //
  // MAX_ATTEMPTS é defesa em profundidade: se algum dia rotateApiUrl
  // retornar true em loop infinito (lista circular acidental, bug),
  // este guard quebra. Valor alto o suficiente pra cobrir N=10 endpoints.
  const MAX_ATTEMPTS = 10
  let res: Response | null = null
  let lastErr: unknown = null
  // Corpo do erro já consumido DENTRO do loop (só no ramo 5xx, pra distinguir
  // "nosso endpoint quebrado" de "provedor fora"). null = ainda não lido; o
  // `!res.ok` abaixo reusa isto em vez de re-ler o stream (que já foi drenado).
  let preReadErrBody: ErrorBody | null = null
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    const base = getApiBase()
    try {
      // `exactOptionalPropertyTypes`: só inclui `signal` quando a global existe.
      const reqInit: RequestInit = net.signal
        ? { ...rest, headers, signal: net.signal }
        : { ...rest, headers }
      res = await Promise.race([fetch(`${base}${path}`, reqInit), net.expired])
      // 5xx: pode ser o NOSSO endpoint quebrado (rotaciona pro próximo) OU o
      // PROVEDOR fora/rate-limitado (503 provider_unavailable). No 2º caso NÃO
      // rotaciona: as bases falam com o MESMO backend (circuito global), então
      // rotacionar só reenviaria o mesmo POST de sign e realimentaria o
      // rate-limit do edge do provedor — exatamente a tempestade que este fix
      // mata. Espia o corpo UMA vez pra decidir (o stream é consumido; guarda
      // em preReadErrBody pro `!res.ok`).
      if (res.status >= 500 && res.status < 600) {
        // Sob o race também: ler o corpo é rede, e um body que não chega
        // penduraria o laço exatamente como o `fetch` penduraria.
        preReadErrBody = (await Promise.race([
          res.json().catch(() => null),
          net.expired,
        ])) as ErrorBody | null
        const providerOutage =
          res.status === 503 && preReadErrBody?.error === 'provider_unavailable'
        if (!providerOutage && rotateApiUrlLazy()) {
          lastErr = new ApiError(`http_${res.status}`, res.status)
          preReadErrBody = null // a próxima tentativa lê o corpo dela
          continue
        }
      }
      // 2xx-4xx, provedor-fora, ou sem próximo endpoint: sai e processa normal
      resetApiUrlLazy() // sucesso/4xx — volta pointer pra primária
      break
    } catch (err) {
      // Prazo estourado (o race) e abort do motor que respeita `signal` são o
      // MESMO desfecho pro caller — e já eram: `AbortError` virava `timeout`.
      // NÃO rotaciona: prazo vencido não é endpoint ruim, e a próxima tentativa
      // nasceria com o orçamento já gasto.
      if (isDeadlineError(err) || (err as Error).name === 'AbortError') {
        net.clear()
        throw new ApiError('timeout', 0)
      }
      lastErr = err
      // Network error (DNS, conn refused, etc): tenta próximo endpoint
      if (rotateApiUrlLazy()) continue
      // Esgotou todos os endpoints — propaga erro original
      net.clear()
      throw err
    }
  }
  net.clear()
  // Saiu do loop sem `res` = MAX_ATTEMPTS estourou (defesa em profundidade
  // contra bug em rotateApiUrl). Propaga último erro ou ApiError genérico.
  if (!res) {
    throw lastErr instanceof Error
      ? lastErr
      : new ApiError('failover_exhausted', 0)
  }

  if (!res.ok) {
    // Reusa o corpo já lido no ramo 5xx do loop; senão lê agora (4xx).
    const data: ErrorBody =
      preReadErrBody ?? ((await res.json().catch(() => ({}))) as ErrorBody)
    const code = data.error ?? `http_${res.status}`

    // 401 que NÃO é sobre a nossa identidade (ver NAO_INVALIDAM_SESSAO): propaga
    // o código pra UI conduzir (configurar fonte / corrigir credencial do painel)
    // SEM clearTokens. Vem ANTES do 401 genérico justamente pra escapar dele.
    if (auth && res.status === 401 && (NAO_INVALIDAM_SESSAO as readonly string[]).includes(code)) {
      throw new ApiError(code, 401)
    }

    // Auth-invalidating errors em rotas autenticadas:
    //  - 401 sempre (token inválido / sessão expirada).
    //  - 403 com `account_expired` (plano Xtream vencido) — user precisa
    //    ser deslogado e ver mensagem "conta expirou", senão fica em loop
    //    de errors em queries autenticadas sem sair pra tela de login.
    // Em ambos casos: clearTokens + notifyAuthInvalid pro app deslogar
    // + redirecionar pra LoginScreen com flashError correta.
    //
    // `silentAuthFailure` (opt-in POR CHAMADA) suprime só o EFEITO COLATERAL,
    // nunca o erro: o caller ainda recebe o ApiError e decide. Existe pra
    // chamadas de FUNDO (best-effort, disparadas sem o usuário pedir) onde
    // deslogar seria um efeito visível vindo de uma ação invisível — o P0 medido:
    // o PUT preference em fire-and-forget logo após o login derrubava a sessão
    // recém-criada, com o gate de invalidação recém-armado pelo próprio
    // `api.login`. Quem chama em PRIMEIRO plano NÃO passa a flag e segue
    // deslogando (é o comportamento certo lá, e o teste-âncora trava isso).
    if (auth && res.status === 401) {
      if (!silentAuthFailure) {
        clearTokens()
        notifyAuthInvalid('session_expired')
      }
      throw new ApiError('unauthorized', 401)
    }
    if (auth && res.status === 403 && code === 'account_expired') {
      if (!silentAuthFailure) {
        clearTokens()
        notifyAuthInvalid('account_expired')
      }
      throw new ApiError('account_expired', 403)
    }
    // PROVEDOR fora/rate-limitado (503 provider_unavailable, docs/specs/api.md
    // §7.4.0). Carrega `retryAfter` (corpo ou header Retry-After) e `reason` pro
    // caller decidir o back-off — o player do ao vivo NÃO rotaciona fontes nesse
    // caso (rotacionar floda o edge do provedor). Ver live-player/provider-outage.ts.
    if (res.status === 503 && code === 'provider_unavailable') {
      const retryAfter = parseRetryAfter(data, res)
      const reason = typeof data.reason === 'string' ? data.reason : undefined
      throw new ApiError('provider_unavailable', 503, {
        ...(retryAfter !== undefined ? { retryAfter } : {}),
        ...(reason !== undefined ? { reason } : {}),
      })
    }
    throw new ApiError(code, res.status)
  }
  return (await res.json()) as T
}

/** Corpo de erro do backend. `retryAfter`/`reason` só vêm no 503 provider_unavailable. */
interface ErrorBody {
  error?: string
  retryAfter?: unknown
  reason?: unknown
}

/** retryAfter em SEGUNDOS: prefere o corpo (`retryAfter`), cai pro header
 *  `Retry-After` (forma numérica). Ignora a forma HTTP-date (backend manda
 *  segundos). undefined se ausente/inválido → o caller usa o back-off default. */
function parseRetryAfter(data: ErrorBody, res: Response): number | undefined {
  const fromBody = data.retryAfter
  if (typeof fromBody === 'number' && Number.isFinite(fromBody) && fromBody >= 0) {
    return fromBody
  }
  // res.headers sempre existe num Response real; guard defensivo pra mocks de teste.
  const hdr = typeof res.headers?.get === 'function' ? res.headers.get('retry-after') : null
  if (hdr) {
    const n = Number(hdr)
    if (Number.isFinite(n) && n >= 0) return n
  }
  return undefined
}

export class ApiError extends Error {
  /** Segundos de back-off pedidos pelo backend (só no 503 provider_unavailable). */
  retryAfter?: number
  /** Motivo do provedor-fora: 'circuit_open' | 'upstream_rate_limited' | 'unreachable'. */
  reason?: string
  constructor(
    public code: string,
    public status: number,
    opts?: { retryAfter?: number; reason?: string },
  ) {
    super(code)
    this.name = 'ApiError'
    // Atribui só quando definido — respeita exactOptionalPropertyTypes.
    if (opts?.retryAfter !== undefined) this.retryAfter = opts.retryAfter
    if (opts?.reason !== undefined) this.reason = opts.reason
  }
}

export const api = {
  // ===== Public =====

  async getBranding(code: string): Promise<ProviderBranding> {
    return request<ProviderBranding>(`/v1/provider/${encodeURIComponent(code)}/branding`)
  },

  async login(
    providerCode: string,
    username: string,
    password: string,
  ): Promise<{ profile?: { username: string }; accountExpiresAt: number | null }> {
    const body = { providerCode, username, password, device: await getDeviceInfo() }
    const res = await request<AuthResponse>('/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify(body),
    })
    setTokens(res.accessToken, res.refreshToken, res.expiresIn)
    resetAuthInvalidGate()
    const out: { profile?: { username: string }; accountExpiresAt: number | null } = {
      accountExpiresAt: res.accountExpiresAt ?? null,
    }
    if (res.profile) out.profile = res.profile
    return out
  },

  async directLogin(
    listName: string,
    url: string,
    username: string,
    password: string,
  ): Promise<{
    branding: ProviderBranding
    providerCode: string
    accountExpiresAt: number | null
  }> {
    const body = { listName, url, username, password, device: await getDeviceInfo() }
    const res = await request<AuthResponse>('/v1/auth/direct', {
      method: 'POST',
      body: JSON.stringify(body),
    })
    setTokens(res.accessToken, res.refreshToken, res.expiresIn)
    resetAuthInvalidGate()
    return {
      // Defensivo: a API sempre devolve `branding` no /v1/auth/direct (ver
      // apps/api/src/routes/auth.ts, primaryColor: '' = "cai em --app-*").
      // Este fallback só roda se o contrato de rede for violado. Era
      // '#3b82f6' (azul cravado) — bug: pintaria a sessão de azul em vez do
      // roxo default do app. primaryColor '' segue a MESMA convenção
      // uniforme-falsy do backend/BrandProvider (ver branding-read.ts).
      branding: res.branding ?? {
        primaryColor: '',
        logoUrl: '',
        name: listName,
        locale: 'pt-BR',
        country: 'BR',
        defaultAudioLanguage: 'dubbed',
        aggressivePreload: true,
      },
      providerCode: res.providerCode ?? 'DIRECT',
      accountExpiresAt: res.accountExpiresAt ?? null,
    }
  },

  /**
   * Revoga o refresh token no servidor (logout de verdade, não só local).
   *
   * `refreshToken` explícito existe pro `sign-out.ts`: lá o token local é
   * limpo ANTES de qualquer I/O (a proteção do próximo usuário da TV não pode
   * depender da rede), então `getTokens()` já voltaria null aqui e a revogação
   * seria silenciosamente pulada. Quem tem o token passa o token. Sem
   * argumento, cai no store (compatível com o uso anterior).
   *
   * Best-effort por desenho: falha de rede não lança. Rota não exige Bearer
   * (o refresh vai no corpo), então funciona mesmo com os tokens já limpos.
   */
  async logout(refreshToken?: string): Promise<void> {
    const token = refreshToken ?? getTokens()?.refreshToken
    if (token) {
      await request('/v1/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refreshToken: token }),
      }).catch(() => undefined)
    }
    clearTokens()
  },

  // ===== Conta cloud Swipe TV (Fase 4A.7) =====
  // Endpoints retornam tokens de **swipeuser** (escopo separado do JWT
  // Xtream que vive em auth-store). Tokens swipeuser vivem em memória
  // do AccountLoginScreen — usados só pra polling de pairing e chamada
  // de preference/resolve. Após resolve, fluxo segue api.login(...)
  // normal com creds Xtream extraídas, que popula auth-store.

  async swipePairingInit(deviceLabel?: string): Promise<{
    code: string
    qrPayload: string
    expiresAt: string
  }> {
    const body: { deviceLabel?: string; deviceInfo?: unknown } = {}
    if (deviceLabel) body.deviceLabel = deviceLabel
    body.deviceInfo = await getDeviceInfo()
    return request('/v1/swipe-account/pairing/init', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },

  async swipePairingStatus(code: string): Promise<
    | { status: 'pending' | 'denied' | 'expired' }
    | { status: 'approved'; accessToken: string; refreshToken: string }
  > {
    return request(`/v1/swipe-account/pairing/${encodeURIComponent(code)}/status`)
  },

  async swipePreferenceResolve(swipeAccessToken: string): Promise<{
    /** 'manual' => o `providerCode` abaixo é o EFÊMERO (`D{hash}`), que identifica
     *  uma lista pessoal e NÃO pode ser reenviado como fonte por código. */
    sourceType: 'code' | 'manual'
    providerCode: string
    xtreamUsername: string
    xtreamPassword: string
    serverUrl: string | null
  }> {
    return request('/v1/swipe-account/preference/resolve', {
      headers: { authorization: `Bearer ${swipeAccessToken}` },
    })
  },

  /**
   * Readiness da conta Swipe (P2 do fluxo de pareamento). Após o device ser
   * aprovado no celular/site, a TV polla este endpoint até `hasSource` virar
   * true (a conta salvou a fonte) e então avança sozinha. É read-only e
   * idempotente (não é uso único como o /pairing/:code/status). Plan:
   * docs/plan/fix-fluxo-pareamento-setup.md (P1/P2).
   */
  async swipeSetupStatus(swipeAccessToken: string): Promise<{
    hasSource: boolean
    profileCount: number
  }> {
    return request('/v1/swipe-account/setup-status', {
      headers: { authorization: `Bearer ${swipeAccessToken}` },
    })
  },

  /**
   * Revoga a sessão SWIPE (o par access+refresh do pareamento) no servidor.
   *
   * NÃO confundir com `api.logout()`: aquele é do universo XTREAM — bate em
   * `/v1/auth/logout` e, de quebra, chama `clearTokens()` no auth-store Xtream.
   * Usar aquele aqui seria errado duas vezes: não revogaria nada (o refresh de
   * swipeuser não é um refresh Xtream) e ainda derrubaria a sessão Xtream de um
   * usuário que já estava logado e só passou pelo gate de vínculo. Os dois
   * mundos são separados por desenho, e a rota certa é esta.
   *
   * `preHandler: swipeAuthGuard` no backend → o access token vai no Bearer e o
   * refresh no corpo (é ele que é revogado).
   *
   * **Best-effort por desenho**: nunca lança. É chamada em caminho de
   * falha/abandono, onde a UI já está indo mostrar outra coisa — uma revogação
   * que não completa não pode virar um segundo erro na cara do usuário, nem
   * segurar a tela esperando a rede.
   */
  async swipeLogout(swipeAccessToken: string, refreshToken: string): Promise<void> {
    await request('/v1/swipe-account/logout', {
      method: 'POST',
      headers: { authorization: `Bearer ${swipeAccessToken}` },
      body: JSON.stringify({ refreshToken }),
    }).catch(() => undefined)
  },

  /**
   * Cadastra/atualiza a FONTE ativa da conta Swipe (A6 mobile / configurar
   * fonte). É o MESMO endpoint que o site usa (apps/site/app/actions/
   * preference.ts): PUT /v1/swipe-account/preference com código do provedor +
   * credenciais Xtream. O backend valida best-effort contra o painel do
   * provedor: `validated:true` => fonte validada (validatedAt!=null) => trial
   * pleno na hora (regra I0). `auth:true` usa o Bearer SWIPE (swipeAuthHeader),
   * que é a identidade do mobile logado.
   *
   * Erros do backend (propagados como ApiError.code): provider_not_found (404),
   * provider_inactive (403), invalid_xtream_credentials (401),
   * xtream_account_expired (403). serverUrl é metadado opcional (URL Xtream do
   * provedor) — não muda a validação, que é por código.
   */
  async setSwipePreference(
    input: {
      providerCode: string
      xtreamUsername: string
      xtreamPassword: string
      serverUrl?: string | undefined
    },
    /** `silentAuthFailure`: pra a chamada de FUNDO do login (ver LoginScreen).
     *  Default = comportamento de sempre (401 desloga), que é o certo pra quem
     *  chama em primeiro plano, com o usuário olhando (MobileConfigureSource). */
    opts: { silentAuthFailure?: boolean } = {},
  ): Promise<{ ok: boolean; validated: boolean; validationSkippedReason: string | null }> {
    const body: Record<string, string> = {
      providerCode: input.providerCode,
      xtreamUsername: input.xtreamUsername,
      xtreamPassword: input.xtreamPassword,
    }
    if (input.serverUrl) body['serverUrl'] = input.serverUrl
    return request('/v1/swipe-account/preference', {
      method: 'PUT',
      body: JSON.stringify(body),
      auth: true,
      ...(opts.silentAuthFailure ? { silentAuthFailure: true } : {}),
    })
  },

  /**
   * Dados da conta Swipe logada (A7 perfil mobile). GET /v1/swipe-account/me
   * com o Bearer SWIPE (`auth:true`). Usado pra mostrar o email da conta na
   * tela de Perfil/Ajustes. Read-only — não persiste nada no cliente.
   */
  async getSwipeAccount(): Promise<{
    id: string
    email: string
    name: string | null
    emailVerified: boolean
    hasPassword: boolean
    hasGoogle: boolean
  }> {
    return request('/v1/swipe-account/me', { auth: true })
  },

  /**
   * Fonte (preferência) ativa da conta Swipe, SEM a senha (A7 perfil mobile).
   * GET /v1/swipe-account/preference com o Bearer SWIPE. Usado pra mostrar a
   * fonte ativa na tela de Perfil. 404 `preference_not_set` quando ainda não há
   * fonte (estado no_provider) — o caller trata como "sem fonte".
   */
  async getSwipePreference(
    /** `silentAuthFailure`: pras leituras de FUNDO (Ajustes, pré-preenchimento da
     *  tela de fonte). Elas rodam sem o usuário pedir; derrubar a sessão dele a
     *  partir daí seria um efeito visível vindo de uma ação invisível. */
    opts: { silentAuthFailure?: boolean } = {},
  ): Promise<{
    /** 'code' = provider cadastrado; 'manual' = URL crua da conta (lista pessoal). */
    sourceType: 'code' | 'manual'
    /**
     * Nome da lista.
     *
     * ⚠️ O BACKEND NÃO MANDA ESTE CAMPO — medido em `be0f6e42`, em `develop` e em
     * `main`: o `GET /v1/swipe-account/preference` responde exatamente
     * `{sourceType, providerCode, xtreamUsername, serverUrl, updatedAt}` (ver o
     * `reply.send` do GET em apps/api/src/routes/swipe-account.ts). Aqui ele chega
     * SEMPRE `undefined`, e quem depender só dele mostra vazio — foi o que
     * aconteceu com o pré-preenchimento do nome da lista e com o card de fonte do
     * mobile. Segue declarado (opcional) porque o cliente já sabe usá-lo no dia em
     * que o contrato incluir; até lá, todo consumidor tem fallback LOCAL.
     *
     * LIMITAÇÃO de desenho, pra quando existir: não há coluna de label em
     * `swipe_user_preferences`, então o nome é por FONTE e não por USUÁRIO. Dois
     * usuários que compartilhem a mesma conta Xtream (mesma url + mesmo username =
     * mesmo provider efêmero) veem o MESMO nome, e o último a renomear vence.
     * Nunca chamar isto de "meu apelido pessoal" na copy.
     */
    label?: string | null
    /** null no modo 'manual' (lá o code é o efêmero D{hash}, de uso interno). */
    providerCode: string | null
    xtreamUsername: string
    serverUrl: string | null
    updatedAt: string
  }> {
    return request('/v1/swipe-account/preference', {
      auth: true,
      ...(opts.silentAuthFailure ? { silentAuthFailure: true } : {}),
    })
  },

  /**
   * MESMO endpoint do `swipePreferenceResolve`, mas com a identidade resolvida
   * pelo `request()` (`auth: true`) em vez de um Bearer passado à mão.
   *
   * Existem os dois porque as duas situações são diferentes: no PAREAMENTO a
   * sessão Swipe ainda não foi instalada no aparelho (o token acabou de chegar do
   * polling), então lá o token vai explícito. Aqui a sessão JÁ está instalada.
   *
   * POR QUE A TELA DE FONTE MANUAL PRECISA DISTO (defeito medido, não preferência):
   * o `PUT preference` responde `{id, ok, validated, validationSkippedReason}` e
   * NÃO devolve `providerCode` — a tela lia `saved.providerCode`, que era
   * `undefined`, e chamava `GET /v1/provider/undefined/branding`. Ou seja, o
   * caminho feliz da tela nova terminava em erro DEPOIS de já ter gravado a fonte
   * na conta. Como o código da fonte manual é EFÊMERO e nasce no servidor, só o
   * servidor pode dizer qual é: este resolve é a única fonte da verdade hoje.
   * (Se o `PUT` passar a devolver o `providerCode`, esta viagem extra sai.)
   */
  async swipePreferenceResolveActive(
    /** `silentAuthFailure`: pra a hidratação de FUNDO do contexto de sync no mobile
     *  (account-context.ts) — mesma razão do `getSwipePreference`. */
    opts: { silentAuthFailure?: boolean } = {},
  ): Promise<{
    sourceType: 'code' | 'manual'
    providerCode: string
    xtreamUsername: string
    xtreamPassword: string
    serverUrl: string | null
  }> {
    return request('/v1/swipe-account/preference/resolve', {
      auth: true,
      ...(opts.silentAuthFailure ? { silentAuthFailure: true } : {}),
    })
  },

  /**
   * Salva a fonte MANUAL (lista pessoal) na CONTA Swipe — `PUT preference` com
   * `sourceType: 'manual'`, Bearer SWIPE.
   *
   * POR QUE NÃO É O `/v1/auth/direct`: aquele é o caminho de DEVICE (provider
   * efêmero amarrado ao aparelho, sessão local). Este amarra a fonte à CONTA, então
   * ela SINCRONIZA entre aparelhos — que foi a razão de o dono escolher este caminho.
   *
   * ⚠️ A RESPOSTA NÃO TEM `providerCode`. Este tipo já afirmou que tinha, e a tela
   * confiou: lia `saved.providerCode` (`undefined`) e pedia
   * `/v1/provider/undefined/branding`. Medido no backend (be0f6e42, develop e
   * main), o `reply.send` do PUT é `{id, ok, validated, validationSkippedReason}` e
   * mais nada. Quem precisa do código EFÊMERO (`D{hash}`, que nasce no servidor)
   * chama `swipePreferenceResolveActive()` — uma viagem a mais que ninguém queria,
   * mas a alternativa era um caminho feliz que termina em erro. Se o backend passar
   * a devolver o código aqui, o resolve extra sai da tela.
   *
   * `validated:false` NÃO é erro: o backend salvou best-effort porque o painel Xtream
   * não respondeu na hora. A fonte existe e o app sai do `no_provider`, mas o caller
   * DEVE avisar o usuário antes de seguir (ver `saved-unverified` na tela).
   */
  async setSwipeSourceManual(input: {
    sourceType: 'manual'
    label: string
    serverUrl: string
    xtreamUsername: string
    xtreamPassword: string
  }): Promise<{
    ok: boolean
    validated: boolean
    validationSkippedReason: string | null
  }> {
    return request('/v1/swipe-account/preference', {
      method: 'PUT',
      body: JSON.stringify(input),
      auth: true,
    })
  },

  // ===== Catalog =====

  async getCategories(): Promise<LiveCategory[]> {
    const local = await readSurface('liveCategories')
    if (local) return local
    const start = performance.now()
    const res = await request<ListEnvelope<LiveCategory>>('/v1/catalog/live/categories', {
      auth: true,
    })
    reportCacheMiss(performance.now() - start)
    return res.items
  },

  async getChannels(categoryId?: string, page = 1, size = 200): Promise<LiveChannel[]> {
    const local = await readSurface('liveChannels', (channels) =>
      categoryId ? channels.filter((c) => c.categoryId === categoryId) : channels,
    )
    if (local) return local
    const qs = new URLSearchParams()
    if (categoryId) qs.set('cat', categoryId)
    qs.set('page', String(page))
    qs.set('size', String(size))
    const start = performance.now()
    const res = await request<ListEnvelope<LiveChannel>>(`/v1/catalog/live/streams?${qs}`, {
      auth: true,
    })
    reportCacheMiss(performance.now() - start)
    return res.items
  },

  async getGroupedChannels(): Promise<GroupedChannel[]> {
    const res = await request<ListEnvelope<GroupedChannel>>('/v1/catalog/live/grouped', {
      auth: true,
    })
    return res.items
  },

  /** Retorna TODOS os canais ao vivo (sem paginação). Usado pela
   *  LiveScreen pra renderizar a guia completa em uma só chamada
   *  (1-3k canais é leve e evita N requests paginados). Backend
   *  cacheia 10min.
   *
   *  LOCAL-FIRST: lê a surface `liveChannels` do snapshot — o MESMO dado que
   *  o /live/all devolve. Paridade garantida no backend (os dois caminhos
   *  chamam getAllLiveChannels + resolveFamilies) e travada pelo teste de
   *  equivalência catalog-service.equivalence.test.ts.
   *
   *  Era a ÚNICA das 5 leitoras de catálogo sem local-first — omissão, não
   *  decisão. O custo era o pior possível: a LiveScreen espera `channelsLoaded`
   *  pra montar o <LiveHero>, então os ~391KB (não comprimidos — a borda da
   *  API não comprime) ficavam NO CAMINHO CRÍTICO do 1º frame de vídeo.
   *
   *  A rede segue como fallback: snapshot ausente/stale, IDB indisponível
   *  (ex: Chr38, onde a lib idb não abre) ou 1º boot pós-install. */
  async getAllChannels(): Promise<LiveChannel[]> {
    const local = await readSurface('liveChannels')
    if (local) return local
    const start = performance.now()
    const res = await request<{ items: LiveChannel[]; total: number }>(
      '/v1/catalog/live/all',
      { auth: true },
    )
    reportCacheMiss(performance.now() - start)
    return res.items
  },

  /** Lista buckets canônicos de canais ao vivo com count.
   *  includeAdult=true só pra adult profile. Cache backend 30min. */
  async getLiveBuckets(
    includeAdult = false,
  ): Promise<Array<{ id: string; icon: string; order: number; count: number }>> {
    const qs = includeAdult ? '?includeAdult=true' : ''
    const res = await request<{
      items: Array<{ id: string; icon: string; order: number; count: number }>
    }>(`/v1/catalog/live/buckets${qs}`, { auth: true })
    return res.items
  },

  /** Taxonomia (labels + ícones) de buckets pelo país do provider.
   *  Usa apps/api/data/country-taxonomy.json — multi-país, i18n. */
  async getLiveTaxonomy(): Promise<{
    country: string
    buckets: Array<{ id: string; label: string; icon: string; order: number }>
  }> {
    return await request<{
      country: string
      buckets: Array<{ id: string; label: string; icon: string; order: number }>
    }>('/v1/catalog/live/taxonomy', { auth: true })
  },

  /** Canais ao vivo de um bucket canônico específico. */
  async getChannelsByBucket(bucketId: string): Promise<LiveChannel[]> {
    const res = await request<{ items: LiveChannel[] }>(
      `/v1/catalog/live/by-bucket/${encodeURIComponent(bucketId)}`,
      { auth: true },
    )
    return res.items
  },

  /** EPG curto batch — retorna Map channelId→{now, next} pra N canais.
   *  Cap N=100 no backend. Usado na LiveScreen pra renderizar
   *  "Agora | A Seguir" sem N requests. */
  async getEpgShortBatch(
    channelIds: string[],
  ): Promise<Record<string, { now: EpgProgram | null; next: EpgProgram | null }>> {
    if (channelIds.length === 0) return {}
    const res = await request<{
      items: Record<string, { now: EpgProgram | null; next: EpgProgram | null }>
    }>('/v1/catalog/live/epg-batch', {
      method: 'POST',
      body: JSON.stringify({ channelIds }),
      auth: true,
    })
    return res.items
  },

  async getVodCategories(): Promise<LiveCategory[]> {
    const local = await readSurface('vodCategories')
    if (local) return local
    const start = performance.now()
    const res = await request<ListEnvelope<LiveCategory>>('/v1/catalog/vod/categories', {
      auth: true,
    })
    reportCacheMiss(performance.now() - start)
    return res.items
  },

  async getVod(
    categoryId?: string,
    page = 1,
    size = 200,
    sort: 'default' | 'popularity' = 'default',
    signal?: AbortSignal,
  ): Promise<VodItem[]> {
    // Sempre vai pro backend: snapshot tem dados CRUS (sem TMDB enrichment).
    // Backend faz JOIN canonical em batch — ~600ms pra 200 items, aceitável.
    // sort=popularity reordena por popularity TMDB DESC (cache separado).
    const qs = new URLSearchParams()
    if (categoryId) qs.set('cat', categoryId)
    qs.set('page', String(page))
    qs.set('size', String(size))
    if (sort !== 'default') qs.set('sort', sort)
    const start = performance.now()
    const res = await request<ListEnvelope<VodItem>>(`/v1/catalog/vod/streams?${qs}`, {
      auth: true,
      ...(signal ? { signal } : {}),
    })
    reportCacheMiss(performance.now() - start)
    return res.items
  },

  /**
   * Lista filmes do Catálogo (modo paginação cumulativa — D.0 do Catálogo
   * v1, 2026-05-11). Cliente pede fatias proporcionais ao tier via
   * capability catalogPageSize. Backend retorna metadata `total`+`hasMore`
   * pra cliente decidir quando parar de paginar.
   *
   * Sem `page`+`size`: retorna catálogo inteiro (compat com scripts/CLI).
   * Com `page`+`size`: fatia de `(page-1)*size` até `page*size`.
   */
  async getAllVod(
    opts: { categoryId?: string; page?: number; size?: number; signal?: AbortSignal } = {},
  ): Promise<{ items: VodItem[]; total: number; offset: number; hasMore: boolean }> {
    const qs = new URLSearchParams({ all: 'true' })
    if (opts.categoryId) qs.set('cat', opts.categoryId)
    if (opts.page !== undefined) qs.set('page', String(opts.page))
    if (opts.size !== undefined) qs.set('size', String(opts.size))
    const start = performance.now()
    const res = await request<{
      items: VodItem[]
      count: number
      total: number
      offset: number
      hasMore: boolean
    }>(`/v1/catalog/vod/streams?${qs}`, {
      auth: true,
      ...(opts.signal ? { signal: opts.signal } : {}),
    })
    reportCacheMiss(performance.now() - start)
    return { items: res.items, total: res.total, offset: res.offset, hasMore: res.hasMore }
  },

  /** Retorna providerCategoryIds ordenados por popularidade TMDB agregada
   *  (top-10 items resolvidos por categoria). Usado na Descobrir pra
   *  reordenar carrosséis. Cache backend 30min. */
  async categoriesByPopularity(
    type: 'vod' | 'series',
  ): Promise<Array<{ categoryId: string; score: number; resolvedCount: number }>> {
    const res = await request<{
      items: Array<{ categoryId: string; score: number; resolvedCount: number }>
    }>(`/v1/catalog/categories-by-popularity?type=${type}`, { auth: true })
    return res.items
  },

  async getVodInfo(id: string): Promise<VodItem> {
    return request<VodItem>(`/v1/catalog/vod/${encodeURIComponent(id)}`, { auth: true })
  },

  /**
   * Lookup batch de cards minimal TMDB. Cliente passa lista de tmdbIds (ex:
   * recommendationsTmdbIds), recebe poster+title+year por id.
   * Backend faz fetch em paralelo + cacheia 30d. Falha individual = item ausente.
   */
  async tmdbMinimalBatch(
    type: 'movie' | 'tv',
    ids: number[],
  ): Promise<Array<{ tmdbId: number; title: string; posterUrl: string | null; year: number | null; rating: number | null }>> {
    if (ids.length === 0) return []
    const res = await request<{ items: Array<{ tmdbId: number; title: string; posterUrl: string | null; year: number | null; rating: number | null }> }>(
      `/v1/tmdb/minimal-batch`,
      { method: 'POST', body: JSON.stringify({ type, ids }), auth: true },
    )
    return res.items
  },

  /**
   * Recomendações filtradas pelo catálogo do parceiro: backend cruza tmdbIds
   * com canonical+match e devolve só os filmes/séries que o parceiro tem,
   * com `sourceId` pra navegar. UX coerente: nunca mostra título inexistente.
   */
  async catalogRecommendations(
    type: 'movie' | 'tv',
    tmdbIds: number[],
  ): Promise<Array<{ tmdbId: number; sourceId: string; title: string; posterUrl: string | null; year: number | null; rating: number | null }>> {
    if (tmdbIds.length === 0) return []
    const res = await request<{ items: Array<{ tmdbId: number; sourceId: string; title: string; posterUrl: string | null; year: number | null; rating: number | null }> }>(
      `/v1/catalog/recommendations`,
      { method: 'POST', body: JSON.stringify({ type, tmdbIds }), auth: true },
    )
    return res.items
  },

  /** Preview de categoria pra CategoryCard (mosaico 2×2). Retorna 4 posters
   *  + total de items. Usado na tab "Categorias" (Onda 7). */
  async vodCategoryPreview(
    catId: string,
    limit = 4,
  ): Promise<{ posters: string[]; total: number }> {
    const qs = new URLSearchParams({ cat: catId, limit: String(limit) })
    return await request<{ posters: string[]; total: number }>(
      `/v1/catalog/vod/category-preview?${qs}`,
      { auth: true },
    )
  },

  /** Preview de categoria de séries (mosaico). Análogo ao vodCategoryPreview. */
  async seriesCategoryPreview(
    catId: string,
    limit = 4,
  ): Promise<{ posters: string[]; total: number }> {
    const qs = new URLSearchParams({ cat: catId, limit: String(limit) })
    return await request<{ posters: string[]; total: number }>(
      `/v1/catalog/series/category-preview?${qs}`,
      { auth: true },
    )
  },

  /** Preview de gênero pra CategoryCard quando reorganizeCatalog=ON.
   *  Análogo a vodCategoryPreview mas filtra por gênero canônico TMDB.
   *  Posters ordenados por popularity (Drama mostra "Cidade de Deus" antes
   *  de obscuros). Backend cache 30min. */
  async genrePreview(
    type: 'movie' | 'tv',
    genre: string,
    limit = 4,
  ): Promise<{ posters: string[]; total: number }> {
    const qs = new URLSearchParams({ type, limit: String(limit) })
    return await request<{ posters: string[]; total: number }>(
      `/v1/catalog/genre-preview/${encodeURIComponent(genre)}?${qs}`,
      { auth: true },
    )
  },

  /** Pega metadados enriquecidos pra lista arbitrária de vodIds.
   *  Usado pra continue assistindo / favoritos (que vêm do local-db
   *  só com IDs). Backend faz JOIN canonical+match e retorna badges.
   *  Todo item carrega `availability` (§7.3.1 de docs/specs/api.md): o
   *  cliente usa `not_found` pra tirar do trilho o que o parceiro removeu. */
  async vodEnrichedBatch(ids: string[]): Promise<EnrichedVodItem[]> {
    if (ids.length === 0) return []
    const res = await request<{ items: EnrichedVodItem[] }>(`/v1/catalog/vod/enriched-batch`, {
      method: 'POST',
      body: JSON.stringify({ ids }),
      auth: true,
    })
    return res.items
  },

  /** Análogo a vodEnrichedBatch pra seriesIds. Resolve poster canônico
   *  da série pra "Continue assistindo" — local-db tem só seriesId +
   *  thumb 16:9 do EP, queremos poster vertical da série. Carrega o mesmo
   *  sinal `availability` do VOD. */
  async seriesEnrichedBatch(ids: string[]): Promise<EnrichedSeriesItem[]> {
    if (ids.length === 0) return []
    const res = await request<{ items: EnrichedSeriesItem[] }>(`/v1/catalog/series/enriched-batch`, {
      method: 'POST',
      body: JSON.stringify({ ids }),
      auth: true,
    })
    return res.items
  },

  async getRecentVod(limit = 30): Promise<VodItem[]> {
    // Sempre vai pro backend: 30 items é leve, e snapshot tem dados CRUS
    // (sem enrichment). Backend retorna lista enriquecida (badges quality,
    // audio, plataforma, ageRating, etc) — vital pra UX correta.
    const start = performance.now()
    const res = await request<ListEnvelope<VodItem>>(`/v1/catalog/vod/recent?limit=${limit}`, {
      auth: true,
    })
    reportCacheMiss(performance.now() - start)
    return res.items
  },

  async getRecentSeries(limit = 30): Promise<Series[]> {
    // Sempre vai pro backend (snapshot tem dados crus). Backend enriquece
    // com canonical+match — UX coerente com tela de detalhes.
    const start = performance.now()
    const res = await request<ListEnvelope<Series>>(`/v1/catalog/series/recent?limit=${limit}`, {
      auth: true,
    })
    reportCacheMiss(performance.now() - start)
    return res.items
  },

  /**
   * Curated rail (Fase 1 taxonomia canônica). Retorna items mistos
   * (vod + series) ou filtrados por kind. Cada item tem `kind` pra
   * cliente rotear navegação corretamente.
   *
   * Cache backend é por (provider, rail, kind, limit) — TTL 1h, invalida
   * quando worker popularity-refresh roda.
   */
  async getCuratedRail(
    railId: string,
    kind: 'mixed' | 'vod' | 'tv' = 'mixed',
    limit = 30,
  ): Promise<CuratedItem[]> {
    const qs = new URLSearchParams({ kind, limit: String(limit) })
    const res = await request<{ id: string; items: CuratedItem[]; count: number }>(
      `/v1/catalog/curated/${railId}?${qs}`,
      { auth: true },
    )
    return res.items
  },

  /**
   * Lista gêneros TMDB disponíveis no provider, ordenados por count DESC.
   * Cliente renderiza chips horizontais. Cache backend 30min.
   */
  async getGenres(type: 'movie' | 'tv'): Promise<Array<{ name: string; count: number }>> {
    const res = await request<{ items: Array<{ name: string; count: number }>; count: number }>(
      `/v1/catalog/genres?type=${type}`,
      { auth: true },
    )
    return res.items
  },

  /**
   * Items de um gênero específico, ordenados por popularity TMDB DESC.
   * Usado quando user clica em chip de gênero pra ver grade completa.
   */
  async getByGenre(
    type: 'movie' | 'tv',
    genre: string,
    limit = 100,
    signal?: AbortSignal,
  ): Promise<CuratedItem[]> {
    const qs = new URLSearchParams({ type, limit: String(limit) })
    const res = await request<{ items: CuratedItem[]; count: number }>(
      `/v1/catalog/by-genre/${encodeURIComponent(genre)}?${qs}`,
      { auth: true, ...(signal ? { signal } : {}) },
    )
    return res.items
  },

  /**
   * Lista items de um gênero (modo paginação cumulativa — D.0). Mesmo
   * contrato de getAllVod/getAllSeries. getByGenre (com limit numérico)
   * continua pra curated rails (top-N por popularity).
   */
  async getAllByGenre(
    type: 'movie' | 'tv',
    genre: string,
    opts: { page?: number; size?: number; signal?: AbortSignal } = {},
  ): Promise<{ items: CuratedItem[]; total: number; offset: number; hasMore: boolean }> {
    const qs = new URLSearchParams({ type, all: 'true' })
    if (opts.page !== undefined) qs.set('page', String(opts.page))
    if (opts.size !== undefined) qs.set('size', String(opts.size))
    const res = await request<{
      items: CuratedItem[]
      count: number
      total: number
      offset: number
      hasMore: boolean
    }>(`/v1/catalog/by-genre/${encodeURIComponent(genre)}?${qs}`, {
      auth: true,
      ...(opts.signal ? { signal: opts.signal } : {}),
    })
    return { items: res.items, total: res.total, offset: res.offset, hasMore: res.hasMore }
  },

  async getSeriesCategories(): Promise<LiveCategory[]> {
    const local = await readSurface('seriesCategories')
    if (local) return local
    const start = performance.now()
    const res = await request<ListEnvelope<LiveCategory>>('/v1/catalog/series/categories', {
      auth: true,
    })
    reportCacheMiss(performance.now() - start)
    return res.items
  },

  async getSeries(
    categoryId?: string,
    page = 1,
    size = 200,
    sort: 'default' | 'popularity' = 'default',
    signal?: AbortSignal,
  ): Promise<Series[]> {
    // Sempre vai pro backend pra ter enrichment via canonical+match.
    // sort=popularity reordena por popularity TMDB DESC (cache separado).
    const qs = new URLSearchParams()
    if (categoryId) qs.set('cat', categoryId)
    qs.set('page', String(page))
    qs.set('size', String(size))
    if (sort !== 'default') qs.set('sort', sort)
    const start = performance.now()
    const res = await request<ListEnvelope<Series>>(`/v1/catalog/series?${qs}`, {
      auth: true,
      ...(signal ? { signal } : {}),
    })
    reportCacheMiss(performance.now() - start)
    return res.items
  },

  /**
   * Análogo a getAllVod pra séries. Paginação cumulativa (D.0).
   */
  async getAllSeries(
    opts: { categoryId?: string; page?: number; size?: number; signal?: AbortSignal } = {},
  ): Promise<{ items: Series[]; total: number; offset: number; hasMore: boolean }> {
    const qs = new URLSearchParams({ all: 'true' })
    if (opts.categoryId) qs.set('cat', opts.categoryId)
    if (opts.page !== undefined) qs.set('page', String(opts.page))
    if (opts.size !== undefined) qs.set('size', String(opts.size))
    const start = performance.now()
    const res = await request<{
      items: Series[]
      count: number
      total: number
      offset: number
      hasMore: boolean
    }>(`/v1/catalog/series?${qs}`, {
      auth: true,
      ...(opts.signal ? { signal: opts.signal } : {}),
    })
    reportCacheMiss(performance.now() - start)
    return { items: res.items, total: res.total, offset: res.offset, hasMore: res.hasMore }
  },

  async getSeriesInfo(id: string): Promise<{
    series: Series
    seasons: Record<string, Array<{
      id: string
      episode_num?: string
      title?: string
      container_extension?: string | null
      season?: number
      info?: {
        plot?: string
        duration_secs?: number
        duration?: string
        movie_image?: string | null
        cover_big?: string | null
        rating?: string | null
        releasedate?: string
      }
    }>>
  }> {
    return request(`/v1/catalog/series/${encodeURIComponent(id)}`, { auth: true })
  },

  async search(
    query: string,
    limit = 20,
    filters?: SearchFiltersClient,
    kind: 'all' | 'vod' | 'series' | 'channels' = 'all',
  ): Promise<SearchResponseClient> {
    // Sempre remote — backend roda enrichVodListPage/enrichSeriesListPage
    // pra incluir hasMultiAudio/quality/originPlatform/etc, padronizando
    // a UI com o resto do catálogo. Snapshot local tem items crus (sem
    // badges) então não serve aqui. Backend usa cache em memória
    // (cat:vod:streams:all), latência ~50-200ms — aceitável pra busca
    // com debounce 300ms.
    //
    // Filtros (v2.1.8.3): CSV multi-valor por grupo. minRating single
    // (7|8|9). kind restringe set de calculo de filtros (Tudo/Filmes/
    // Series/Canais — sem isso, filtro sobre set escondido nao reflete
    // visualmente). Backend retorna availableFilters dinamico (chips
    // com items no resultset, EXCETO o proprio facet).
    const qs = new URLSearchParams({ q: query, limit: String(limit) })
    if (kind !== 'all') qs.set('kind', kind)
    if (filters?.genres?.length) qs.set('genres', filters.genres.join(','))
    if (filters?.years?.length) qs.set('years', filters.years.join(','))
    if (filters?.platforms?.length) qs.set('platforms', filters.platforms.join(','))
    if (filters?.ageRatings?.length) qs.set('ageRatings', filters.ageRatings.join(','))
    if (filters?.audios?.length) qs.set('audios', filters.audios.join(','))
    if (filters?.qualities?.length) qs.set('qualities', filters.qualities.join(','))
    if (filters?.minRating != null) qs.set('minRating', String(filters.minRating))
    const start = performance.now()
    const out = await request<SearchResponseClient>(
      `/v1/catalog/search?${qs}`,
      { auth: true },
    )
    reportCacheMiss(performance.now() - start)
    return out
  },

  async getEpgShort(streamId: string): Promise<EpgProgram[]> {
    const res = await request<ListEnvelope<EpgProgram>>(
      `/v1/catalog/epg/short?stream=${encodeURIComponent(streamId)}`,
      { auth: true },
    )
    return res.items
  },

  async getEpgFull(streamId: string): Promise<EpgProgram[]> {
    const res = await request<ListEnvelope<EpgProgram>>(
      `/v1/catalog/epg/full?stream=${encodeURIComponent(streamId)}`,
      { auth: true },
    )
    return res.items
  },

  // ===== Stream sign =====

  async signStream(
    type: 'live' | 'vod' | 'series' | 'catchup',
    streamId: string,
    // F3 — `avoidEndpointId`: endpoint que o player pede pra EVITAR no re-sign.
    // Quando o stream cai na reprodução (erro de mídia / stall) e o app esgota as
    // fontes do canal, ele re-chama o sign passando o endpointId do sign atual; o
    // backend pula esse endpoint e resolve o próximo por priority (failover
    // client-side, espelho do auto-switch de família).
    extras: { ext?: string; start?: number; duration?: number; avoidEndpointId?: string } = {},
    // `critical: false` = sign de BACKGROUND (prefetch/probe): ninguém está
    // esperando um frame por causa dele. Default true — quem não declara é
    // playback, e o custo de errar pro lado seguro é telemetria adiada.
    opts: { critical?: boolean } = {},
  ): Promise<SignedStreamUrl> {
    // JANELA CRÍTICA DE REDE (net-priority) — marcada AQUI, no funil de todo
    // sign, e não no `signStreamDeduped`: só o live passava pelo dedup, então
    // VOD/série/catchup/mobile (que chamam este método direto) davam play SEM
    // janela nenhuma — a telemetria competia à vontade com o play deles.
    // Marcando no funil, qualquer caminho de sign fica coberto por construção.
    const critical = opts.critical ?? true
    if (!critical) {
      const fpBg = (await getDeviceInfo()).primaryId
      return request<SignedStreamUrl>('/v1/stream/sign', {
        method: 'POST',
        body: JSON.stringify({ type, streamId, deviceFingerprint: fpBg, ...extras }),
        auth: true,
      })
    }

    beginCritical()
    // `signed` decide se o hold de buffering começa: um sign que FALHOU não vai
    // tocar vídeo nenhum, então segurar 8s de janela por causa dele só represa
    // telemetria à toa. Ver endCritical.
    let signed = false
    try {
      // deviceFingerprint: o backend usa pra checar cobertura/trial server-side antes de
      // assinar (blindagem). End-user por código não tem conta cloud → identifica pelo fp.
      const fp = (await getDeviceInfo()).primaryId
      const value = await request<SignedStreamUrl>('/v1/stream/sign', {
        method: 'POST',
        body: JSON.stringify({ type, streamId, deviceFingerprint: fp, ...extras }),
        auth: true,
      })
      signed = true
      return value
    } finally {
      // A janela NÃO fecha aqui quando `signed`: o hold de buffering segue até o
      // 1º frame (`notifyPlaybackStarted`) ou o teto. Ver net-priority.
      endCritical({ startPlaybackHold: signed })
    }
  },

  // QoE saiu daqui de propósito: telemetria NÃO usa o `request()` do app (que
  // tem failover/retry e pode rotacionar o endpoint global) nem o auth que pode
  // deslogar. Ver `telemetry-transport.ts` + `qoe.ts`.

  // ===== Stream session (sub-onda εd) — simultaneidade + trial =====
  // Funciona pra conta (auth) e anônimo (fingerprint no body).
  //
  // ⚠️ ESTE BLOCO NÃO É TELEMETRIA. Parece (é periódico, tem cara de "ping"),
  // mas é o par que AUTORIZA e CONTABILIZA o playback. Evidência no backend
  // (apps/api/src/routes/stream-session.ts):
  //
  //  - `start` L114-116: `phase === 'blocked'` → **402**, nega o play.
  //  - `start` L123-125 + L144-150: pré-check e recontagem pós-insert do limite
  //    de telas do plano → **409 stream_limit_reached**.
  //  - `start` L129-139: **cria a linha** em `stream_sessions` — essa linha É o
  //    contador de simultâneas (`countActiveStreams` conta `ended_at IS NULL`
  //    com heartbeat fresco).
  //  - `heartbeat` L209-215: **único caller de `addTrialSeconds` no backend
  //    inteiro** — é ele que debita o trial. E L223-229: quando estoura, encerra
  //    a sessão e devolve `blocked` (o app interrompe o vídeo em execução).
  //  - `/v1/stream/sign` NÃO recebe `streamSessionId` (não está no
  //    `StreamSignBody` de routes/schemas.ts): a checagem de trial que ele faz
  //    lê `secondsUsedToday`, que só se move via heartbeat.
  //
  // Ou seja: sem `start` → sem id → sem heartbeat → simultâneas sem enforcement
  // E trial infinito, com o play liberado. Por isso `start` NÃO cede a vez a
  // nada — ele está no caminho crítico do play e o usuário espera por ele.
  async streamSessionStart(): Promise<{ streamSessionId: string; phase: TrialPhase; remainingSeconds: number | null }> {
    const fp = (await getDeviceInfo()).primaryId
    return request('/v1/trial/session/start', {
      method: 'POST',
      body: JSON.stringify({ deviceFingerprint: fp }),
      auth: true,
    })
  },

  async streamSessionHeartbeat(
    streamSessionId: string,
  ): Promise<{ phase: TrialPhase; remainingSeconds: number | null }> {
    // FUNCIONAL ADIÁVEL — não é telemetria (ver bloco acima): debita o trial,
    // mantém viva a vaga de simultaneidade e é o gatilho do fail-closed do
    // cliente (3 falhas seguidas → bloqueia, anti-bypass em useTrialGuard).
    //
    // Mas ninguém ESPERA por ele: é um tick de 30s em background. Então cede a
    // vez ao caminho crítico via `runLowPriority` — que, ao contrário da
    // telemetria pura, mantém um teto de defesa em profundidade.
    //
    // Adiar é seguro: o backend calcula o tempo assistido pelo DELTA REAL entre
    // heartbeats (stream-session.ts L196-197), com clamp de 90s. Um heartbeat
    // atrasado não perde contabilidade — o próximo cobre o intervalo inteiro.
    // Por isso NÃO coalescemos heartbeats: cada tick é liveness + contabilidade
    // de UMA sessão, não uma amostra descartável.
    return runLowPriority(async () => {
      const fp = (await getDeviceInfo()).primaryId
      return request('/v1/trial/session/heartbeat', {
        method: 'POST',
        body: JSON.stringify({ streamSessionId, deviceFingerprint: fp }),
        auth: true,
      })
    })
  },

  async streamSessionEnd(streamSessionId: string): Promise<void> {
    // FUNCIONAL: libera a vaga de simultaneidade. Best-effort no teardown — se
    // falhar, a vaga expira sozinha quando o heartbeat envelhece.
    await request('/v1/trial/session/end', {
      method: 'POST',
      body: JSON.stringify({ streamSessionId }),
      auth: true,
    }).catch(() => undefined) // best-effort no teardown
  },

  // Estado do trial SEM abrir stream — pro gate global checar no boot e bloquear
  // o app inteiro se o trial do dia já esgotou. Leve (não conta tempo).
  async trialStatus(): Promise<{ phase: TrialPhase; remainingSeconds: number | null }> {
    const fp = (await getDeviceInfo()).primaryId
    const qs = fp ? `?deviceFingerprint=${encodeURIComponent(fp)}` : ''
    return request(`/v1/trial/status${qs}`, { auth: true })
  },

  // Cria o contexto de compra (overlay de trial/limite) → URL contextual pro QR.
  // Manda o contexto do provider que o user usava (sem senha — segredo nunca
  // vai no QR). auth:true → se logado, backend vincula o swipeUserId.
  async createPurchaseContext(input: {
    source: 'trial' | 'stream_limit'
    providerCode?: string | null
    listName?: string | null
    serverUrl?: string | null
    xtreamUsername?: string | null
  }): Promise<{ code: string; url: string }> {
    const fp = (await getDeviceInfo()).primaryId
    return request('/v1/trial/purchase-context', {
      method: 'POST',
      body: JSON.stringify({
        source: input.source,
        deviceFingerprint: fp,
        ...(input.providerCode ? { providerCode: input.providerCode } : {}),
        ...(input.listName ? { listName: input.listName } : {}),
        ...(input.serverUrl ? { serverUrl: input.serverUrl } : {}),
        ...(input.xtreamUsername ? { xtreamUsername: input.xtreamUsername } : {}),
      }),
      auth: true,
    })
  },
}

export type TrialPhase = 'full' | 'limited' | 'blocked'
